import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Linkedin, Mail } from 'lucide-react';
import { FundleLogo } from './FundleLogo';

// Editorial Brain-centric footer (Oct 2026) — clean 5-column layout
// matching the fundle-brain-rebuild reference. White bg, navy headings,
// slate links, no fake social proof.

const COLS = [
  {
    heading: 'Fundle Brain',
    links: [
      { l: 'The AI engine', h: '/products/brain' },
      { l: 'Explore the agents', h: '/customers' },
      { l: 'Connect your data', h: '/platform' },
      { l: 'Data & access', h: '/legal/privacy' },
      { l: 'Responsible AI', h: '/responsible-ai' },
    ],
  },
  {
    heading: 'Retail',
    links: [
      { l: 'Loyalty & retention', h: '/products/loyalty' },
      { l: 'Campaign intelligence', h: '/products/brain' },
      { l: 'MBO & SIS', h: '/solutions/brands' },
      { l: 'Marketplace reconciliation', h: '/products/adsr' },
      { l: 'All retail workflows', h: '/retail-ai' },
    ],
  },
  {
    heading: 'Malls',
    links: [
      { l: 'ADSR & tenant sales', h: '/products/adsr' },
      { l: 'Mall loyalty', h: '/solutions/malls' },
      { l: 'AI concierge', h: '/products/brain' },
      { l: 'Retail media', h: '/products/reach' },
      { l: 'All mall workflows', h: '/mall-ai' },
    ],
  },
  {
    heading: 'Explore',
    links: [
      { l: 'Agent stories', h: '/customers' },
      { l: 'Guides', h: '/resources' },
      { l: 'Industries', h: '/solutions' },
      { l: 'About & leadership', h: '/about' },
      { l: 'Contact', h: '/contact' },
    ],
  },
];

const FooterZoho = () => (
  <footer className="bg-white border-t border-slate-100" data-testid="footer-root">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10 lg:gap-8">
        <div className="col-span-2">
          <FundleLogo />
          <p className="text-slate-600 text-[13.5px] leading-relaxed mt-5 max-w-xs">
            The AI engine for retail and malls.
          </p>
          <a
            href="mailto:hello@fundle.ai"
            className="inline-flex items-center gap-1.5 mt-4 text-[13px] text-[#2563EB] hover:text-[#1D4ED8] font-semibold"
            data-testid="footer-email"
          >
            <Mail size={13} /> hello@fundle.ai
          </a>
          <div className="flex items-center gap-2 mt-6">
            <a
              href="https://www.linkedin.com/company/fundle-ai"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="w-9 h-9 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:text-[#2563EB] hover:border-[#2563EB] transition-colors"
              data-testid="footer-social-linkedin"
            >
              <Linkedin size={14} strokeWidth={1.8} />
            </a>
          </div>
        </div>

        {COLS.map((col) => (
          <div key={col.heading}>
            <p className="text-[#14223D] font-semibold text-[14px] mb-4">{col.heading}</p>
            <ul className="space-y-2.5">
              {col.links.map((link) => (
                <li key={link.h}>
                  <Link
                    to={link.h}
                    className="inline-flex items-center gap-1 text-slate-600 hover:text-[#2563EB] text-[13.5px] transition-colors group"
                  >
                    {link.l}
                    <ArrowUpRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={2.2} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="pt-10 mt-16 border-t border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <p className="text-slate-500 text-[12.5px]">
          © {new Date().getFullYear()} Fundle.ai. Made in India for Indian retail.
        </p>
        <div className="flex items-center gap-5 text-slate-500 text-[12.5px]">
          <Link to="/legal/privacy" className="hover:text-[#2563EB] transition-colors">Privacy</Link>
          <Link to="/legal/terms" className="hover:text-[#2563EB] transition-colors">Terms</Link>
          <Link to="/legal/dpdp" className="hover:text-[#2563EB] transition-colors">DPDP</Link>
          <Link to="/responsible-ai" className="hover:text-[#2563EB] transition-colors">Responsible AI</Link>
        </div>
      </div>
    </div>
  </footer>
);

export default FooterZoho;
